import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import crmApi from '../../utils/crmApi';

const FIELDS = [
  { key: 'company_name', label: 'Company Name', group: 'Company (letterhead)' },
  { key: 'company_po_box', label: 'P.O. Box line', group: 'Company (letterhead)' },
  { key: 'company_city', label: 'City / Country line', group: 'Company (letterhead)' },
  { key: 'company_phone', label: 'Phone', group: 'Company (letterhead)' },
  { key: 'company_vatin', label: 'VATIN', group: 'Company (letterhead)' },
  { key: 'payment_account_name', label: 'Account Name', group: 'Payment details' },
  { key: 'payment_bank_name', label: 'Bank Name', group: 'Payment details' },
  { key: 'payment_account_number', label: 'Account Number', group: 'Payment details' },
  { key: 'payment_iban', label: 'IBAN (optional)', group: 'Payment details' },
];

const TOGGLES = [
  { key: 'notify_enabled', label: 'Enable email notifications', hint: 'Master switch for all CRM emails.' },
  { key: 'notify_approval_request', label: 'Approval requests', hint: 'When a document or forecast needs approval.' },
  { key: 'notify_order_approved', label: 'Service order approved', hint: 'When a service order is confirmed & approved.' },
  { key: 'notify_quote_sent', label: 'Quote sent', hint: 'When a quote is sent to a customer.' },
  { key: 'notify_invoice_due', label: 'Invoice due reminders', hint: 'When an invoice reaches its due date.' },
  { key: 'notify_invoice_paid', label: 'Invoice paid', hint: 'When an invoice is marked paid.' },
  { key: 'notify_forecast_freeze', label: 'Monthly freeze approvals', hint: 'When a rolling-forecast month is submitted to freeze.' },
  { key: 'notify_baseline_approved', label: 'Baseline approved', hint: 'When an annual baseline forecast is approved.' },
  { key: 'notify_deal_won', label: 'Deal won', hint: 'When a deal is marked won.' },
  { key: 'notify_deal_lost', label: 'Deal lost', hint: 'When a deal is marked lost.' },
  { key: 'notify_low_stock', label: 'Low stock alerts', hint: 'When a product reaches its reorder level.' },
  { key: 'notify_new_account', label: 'New account created', hint: 'When a new customer account is added.' },
];

const SMTP = [
  { key: 'smtp_host', label: 'SMTP Host', placeholder: 'smtp.office365.com' },
  { key: 'smtp_port', label: 'Port', placeholder: '587' },
  { key: 'smtp_user', label: 'Username', placeholder: 'no-reply@yourcompany.com' },
  { key: 'smtp_pass', label: 'Password', type: 'password', placeholder: '........' },
  { key: 'smtp_from', label: 'From address', placeholder: 'CRM <no-reply@yourcompany.com>' },
];

export default function CrmSettings() {
  const { isAdmin } = useOutletContext();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState('company');

  useEffect(() => { crmApi.get('/settings').then(setForm).finally(() => setLoading(false)); }, []);

  async function save() {
    await crmApi.put('/settings', form);
    setSaved(true); setTimeout(() => setSaved(false), 2500);
  }
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const on = (k) => String(form[k]) === '1';

  if (!isAdmin) return <div style={{ color: 'var(--muted)' }}>Only a CRM admin can configure settings.</div>;
  if (loading) return <div style={{ color: 'var(--muted)' }}>Loading...</div>;

  const companyGroups = [...new Set(FIELDS.map(f => f.group))];
  const TABS = [['company', 'Company & Payment'], ['email', 'Email Notifications'], ['smtp', 'Email (SMTP) Setup']];

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800 }}>Settings</h1>
        <p style={{ fontSize: 13, color: 'var(--muted)' }}>Company details for documents, plus email notifications and the mail server used to send them.</p>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'var(--surface2)', padding: 4, borderRadius: 12, width: 'fit-content' }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            fontSize: 13, fontWeight: 700, padding: '8px 16px', borderRadius: 9, cursor: 'pointer', border: 'none',
            background: tab === k ? 'var(--surface)' : 'transparent', color: tab === k ? '#0D9488' : 'var(--muted2)' }}>{l}</button>
        ))}
      </div>

      {tab === 'company' && companyGroups.map(g => (
        <div key={g} className="card" style={{ padding: 18, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14 }}>{g}</div>
          {FIELDS.filter(f => f.group === g).map(f => (
            <div className="form-group" key={f.key}>
              <label className="form-label">{f.label}</label>
              <input className="input" value={form[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
            </div>
          ))}
        </div>
      ))}

      {tab === 'email' && (
        <div className="card" style={{ padding: 18, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Notifications</div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>Choose which events send an email. The master switch must be on for any to send.</p>
          {TOGGLES.map(t => (
            <label key={t.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
              <input type="checkbox" checked={on(t.key)} onChange={e => set(t.key, e.target.checked ? '1' : '0')} style={{ marginTop: 3 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{t.hint}</div>
              </div>
            </label>
          ))}
          <div className="form-group" style={{ marginTop: 16 }}>
            <label className="form-label">Notify these addresses (comma-separated)</label>
            <input className="input" placeholder="manager@company.com, finance@company.com" value={form.notify_to || ''} onChange={e => set('notify_to', e.target.value)} />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Used for general notifications. Approval emails still go to the assigned approver.</div>
          </div>
        </div>
      )}

      {tab === 'smtp' && (
        <div className="card" style={{ padding: 18, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Outgoing mail server (SMTP)</div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>The mailbox the CRM uses to send notifications. Leave blank to use the server's environment configuration.</p>
          {SMTP.map(f => (
            <div className="form-group" key={f.key}>
              <label className="form-label">{f.label}</label>
              <input className="input" type={f.type || 'text'} placeholder={f.placeholder || ''} value={form[f.key] || ''} onChange={e => set(f.key, e.target.value)} autoComplete="off" />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={save}>Save Settings</button>
        {saved && <span style={{ fontSize: 13, color: '#16A34A', fontWeight: 600 }}>Saved</span>}
      </div>
    </div>
  );
}
